import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentKeys } from "./agent";
import type { ToolCallEntryData } from "@agentic_system/components/tool-call-entry";

export interface AgentSchema {
  id: number;
  version: number;
  prompt: string;
  schema: Record<string, unknown>;
  example: Record<string, unknown>;
  example_tool_calls: ToolCallEntryData[];
  status: "pending" | "processing" | "completed" | "error";
  error: string | null;
  duration_ms: number | null;
  render_spec: Record<string, unknown> | null;
  active: boolean;
  created_at: string;
}

const schemasUrl = (agentId: number) =>
  `/system/agentic/agents/${agentId}/schemas`;

export const agentSchemaApi = {
  list: async (agentId: number): Promise<AgentSchema[]> => {
    const { data } = await axios.get(`${schemasUrl(agentId)}.json`);
    return data;
  },

  create: async (agentId: number, input: { prompt: string; profile_id?: number; profile_class?: string }): Promise<AgentSchema> => {
    const { data } = await axios.post(`${schemasUrl(agentId)}.json`, input);
    return data;
  },

  activate: async (agentId: number, schemaId: number): Promise<AgentSchema> => {
    const { data } = await axios.patch(
      `${schemasUrl(agentId)}/${schemaId}/activate.json`,
    );
    return data;
  },

  cancel: async (agentId: number, schemaId: number): Promise<AgentSchema> => {
    const { data } = await axios.patch(
      `${schemasUrl(agentId)}/${schemaId}/cancel.json`,
    );
    return data;
  },

  rerunPreview: async (
    agentId: number,
    schemaId: number,
    input: { profile_id?: number; profile_class?: string } = {},
  ): Promise<AgentSchema> => {
    const { data } = await axios.patch(
      `${schemasUrl(agentId)}/${schemaId}/rerun_preview.json`,
      input,
    );
    return data;
  },
};

export const agentSchemaKeys = {
  all: (agentId: number) => ["agents", agentId, "schemas"] as const,
};

export function useAgentSchemas(agentId: number) {
  return useQuery({
    queryKey: agentSchemaKeys.all(agentId),
    queryFn: () => agentSchemaApi.list(agentId),
    enabled: !!agentId,
  });
}

export function useCreateAgentSchema(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { prompt: string; profile_id?: number; profile_class?: string }) => agentSchemaApi.create(agentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
    },
  });
}

export function useCancelAgentSchema(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemaId: number) => agentSchemaApi.cancel(agentId, schemaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
    },
  });
}

export function useRerunAgentSchemaPreview(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { schemaId: number; profile_id?: number; profile_class?: string }) =>
      agentSchemaApi.rerunPreview(agentId, input.schemaId, {
        profile_id: input.profile_id,
        profile_class: input.profile_class,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
    },
  });
}

export function useActivateAgentSchema(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schemaId: number) => agentSchemaApi.activate(agentId, schemaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentSchemaKeys.all(agentId) });
      queryClient.invalidateQueries({ queryKey: agentKeys.all });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
    },
  });
}
